import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  usePlatformSettings,
  useUpdatePlatformSettings,
  type UpdatePlatformSettingsInput,
} from "@/hooks/usePlatformSettings";

export const MaintenanceSection = () => {
  const { data, isLoading } = usePlatformSettings();
  const update = useUpdatePlatformSettings();

  const [form, setForm] = useState({
    maintenanceMode: false,
    maintenanceMessageEn: "",
    maintenanceMessageMl: "",
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      maintenanceMode: Boolean(data.maintenanceMode),
      maintenanceMessageEn: data.maintenanceMessageEn ?? "",
      maintenanceMessageMl: data.maintenanceMessageMl ?? "",
    });
  }, [data]);

  const onSave = () => {
    const payload: UpdatePlatformSettingsInput = {
      maintenanceMode: form.maintenanceMode,
      maintenanceMessageEn: form.maintenanceMessageEn.trim() || null,
      maintenanceMessageMl: form.maintenanceMessageMl.trim() || null,
    };
    update.mutate(payload, {
      onSuccess: () =>
        toast.success(
          form.maintenanceMode
            ? "Maintenance mode ON — non-admin traffic blocked"
            : "Maintenance mode OFF",
        ),
      onError: (e) => toast.error(e.message),
    });
  };

  if (isLoading) return <Skeleton className="h-40 w-full" />;

  const active = form.maintenanceMode;

  return (
    <section
      className={`rounded-card border p-5 space-y-4 ${
        active
          ? "border-red-400 bg-red-50 dark:bg-red-950/20"
          : "border-border bg-card"
      }`}
    >
      <header className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            {active && <AlertTriangle className="h-5 w-5 text-red-600" />}
            Maintenance mode
          </h2>
          <p className="text-sm text-muted-foreground">
            When on, all non-admin API traffic is blocked with a 503 and the
            bilingual message below. Admin panel stays functional. Toggle takes
            effect within ~30 seconds.
          </p>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <Switch
            checked={form.maintenanceMode}
            onCheckedChange={(checked) =>
              setForm({ ...form, maintenanceMode: checked })
            }
          />
          <Label className="text-sm">{active ? "ON" : "OFF"}</Label>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Message (English)">
          <Textarea
            rows={3}
            placeholder="We're performing scheduled maintenance. Please check back shortly."
            value={form.maintenanceMessageEn}
            onChange={(e) =>
              setForm({ ...form, maintenanceMessageEn: e.target.value })
            }
          />
        </Field>
        <Field label="Message (Malayalam)">
          <Textarea
            rows={3}
            placeholder="ഞങ്ങൾ പരിപാലനം നടത്തുന്നു. കുറച്ച് സമയം കഴിഞ്ഞ് വീണ്ടും ശ്രമിക്കുക."
            value={form.maintenanceMessageMl}
            onChange={(e) =>
              setForm({ ...form, maintenanceMessageMl: e.target.value })
            }
          />
        </Field>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={onSave}
          disabled={update.isPending}
          variant={active ? "destructive" : "default"}
        >
          {update.isPending
            ? "Saving…"
            : active
              ? "Save (maintenance ON)"
              : "Save"}
        </Button>
      </div>
    </section>
  );
};

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    {children}
  </div>
);

export default MaintenanceSection;
